import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { SandboxForm } from '../../../features/hero'
import { loadAuth } from '../../../lib/authStorage'

const CONTACT_EMAIL = 'hello@usenudge.io'

// Hand-drawn squiggle under headline — Mailchimp-style accent
function Squiggle({ color = 'var(--accent)' }: { color?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 240 14"
      width="220"
      height="14"
      fill="none"
      style={{ display: 'block', marginTop: 6 }}
    >
      <path
        d="M2 9 C 30 -2, 60 14, 90 6 S 150 -2, 180 8 S 230 14, 238 4"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default function HeroSection() {
  const shouldReduceMotion = useReducedMotion()
  const [copied, setCopied] = useState(false)
  // Hero CTA flips based on auth: acquisition copy for guests, contextual
  // "Open dashboard" for returning signed-in users.
  const isSignedIn = !!loadAuth()?.accessToken

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = CONTACT_EMAIL
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
      } catch {
        /* noop */
      }
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <section
      id="hero-section"
      className="relative px-6"
      style={{
        backgroundColor: '#FBE6D4',
        borderBottom: '2px solid #111',
      }}
    >
      {/* Spot doodle — paper plane, sending a nudge */}
      <svg
        aria-hidden
        className="absolute top-28 right-10 hidden lg:block"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
      >
        {/* Plane body */}
        <path d="M58 6 L4 26 L20 54 Z" stroke="#111" strokeWidth="2.5" strokeLinejoin="round" />
        {/* Fold line */}
        <line x1="4" y1="26" x2="32" y2="38" stroke="#111" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <div
        aria-hidden
        className="absolute bottom-3 left-10 hidden lg:flex items-end gap-2"
      >
        <span
          className="font-dm-mono font-bold text-xs uppercase pb-1"
          style={{ color: '#111', letterSpacing: '0.16em' }}
        >
          Live updates
        </span>
        <svg width="44" height="48" viewBox="0 0 44 48" fill="none">
          <path
            d="M4 4 C 16 18, 22 30, 24 44 M 24 44 L 18 38 M 24 44 L 30 38"
            stroke="#111"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>

      <div
        className="max-w-6xl mx-auto relative flex items-center"
        style={{
          minHeight: 'calc(100vh - 96px)',
          paddingTop: 120,
          paddingBottom: 96,
        }}
      >
        <div className="w-full grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — copy */}
          <div className="lg:col-span-7 space-y-6">
            <motion.h1
              {...(shouldReduceMotion
                ? {}
                : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } })}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="font-extrabold tracking-tight"
              style={{
                color: '#111',
                fontSize: 'clamp(2.5rem, 5.4vw, 4.75rem)',
                lineHeight: 1,
                letterSpacing: '-0.035em',
              }}
            >
              Your best customers forgot about you.{' '}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                Remind them.
                <Squiggle />
              </span>
            </motion.h1>

            <motion.p
              {...(shouldReduceMotion ? {} : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } })}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-lg md:text-xl max-w-[600px]"
              style={{ color: '#3a2e26', lineHeight: 1.5 }}
            >
              Nudge watches your customer list, spots who hasn't been back in a while, and
              sends them a warm personal message — automatically.
            </motion.p>

            <motion.div
              {...(shouldReduceMotion ? {} : { initial: { opacity: 0 }, animate: { opacity: 1 } })}
              transition={{ duration: 0.5, delay: 0.22 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link
                to={isSignedIn ? '/dashboard' : '/signup'}
                className="hero-cta-primary inline-flex items-center gap-2 transition-transform duration-150"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff',
                  padding: '14px 28px',
                  borderRadius: 999,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  border: '2px solid #111',
                  boxShadow: '4px 4px 0 #111',
                }}
              >
                {isSignedIn ? 'Open dashboard' : 'Start free'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M13 7l5 5-5 5M6 12h12"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <a
                href="#how-it-works"
                className="hero-cta-secondary inline-flex items-center gap-1.5"
                style={{
                  padding: '14px 8px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                See how it works
                <span className="hero-cta-arrow" aria-hidden>→</span>
              </a>
            </motion.div>

            {!isSignedIn && (
              <motion.div
                {...(shouldReduceMotion ? {} : { initial: { opacity: 0 }, animate: { opacity: 1 } })}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold pt-2"
                style={{ color: '#3a2e26' }}
              >
                {['Free plan, forever', '14-day Pro trial', 'No credit card'].map(item => (
                  <span key={item} className="flex items-center gap-1.5">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#111"
                      strokeWidth={3}
                      aria-hidden
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </span>
                ))}
              </motion.div>
            )}
          </div>

          {/* Right — sandbox in flat bordered card */}
          <motion.div
            {...(shouldReduceMotion
              ? {}
              : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } })}
            transition={shouldReduceMotion ? undefined : { duration: 0.55, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div
              className="hero-sandbox-card"
              style={{
                backgroundColor: '#fff',
                border: '2px solid #111',
                borderRadius: 20,
                padding: 4,
                boxShadow: '8px 8px 0 #111',
              }}
            >
              <SandboxForm />
            </div>

            <p
              className="mt-6 flex items-center justify-center gap-2 text-sm"
              style={{ color: '#3a2e26' }}
            >
              Got a question?{' '}
              <button
                type="button"
                onClick={handleCopyEmail}
                className="font-bold underline-offset-4 hover:underline transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                style={{ color: '#111', background: 'none', border: 'none', padding: 0 }}
                aria-label={copied ? 'Email copied to clipboard' : `Copy ${CONTACT_EMAIL} to clipboard`}
              >
                {CONTACT_EMAIL}
                {copied && (
                  <span className="text-[0.7rem] font-medium" style={{ color: '#16a34a' }}>
                    ✓ Copied
                  </span>
                )}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      <style>{`
        .hero-cta-primary:hover {
          transform: translate(-2px, -2px);
          box-shadow: 6px 6px 0 #111 !important;
        }
        .hero-cta-primary:active {
          transform: translate(2px, 2px);
          box-shadow: 0 0 0 #111 !important;
        }
        .hero-cta-secondary {
          color: #111;
          border-bottom: 2px solid #111;
          transition: color 150ms ease, border-color 150ms ease;
        }
        .hero-cta-secondary:hover {
          color: var(--accent);
          border-bottom-color: var(--accent);
        }
        .hero-cta-arrow {
          display: inline-block;
          transition: transform 150ms ease;
        }
        .hero-cta-secondary:hover .hero-cta-arrow {
          transform: translateX(4px);
        }

        /* Electricity splash — card shadow surges orange then snaps back */
        @keyframes hero-card-zap {
          0%, 75%, 100% {
            box-shadow: 8px 8px 0 #111;
            transform: none;
          }
          77% {
            box-shadow: 8px 8px 0 var(--accent);
            transform: translate(-2px, -2px);
          }
          80% {
            box-shadow: 10px 10px 0 var(--accent);
            transform: translate(2px, 2px);
          }
          83% {
            box-shadow: 8px 8px 0 var(--accent);
            transform: translate(-1px, -1px);
          }
          87% {
            box-shadow: 8px 8px 0 #111;
            transform: none;
          }
        }
        .hero-sandbox-card {
          animation: hero-card-zap 10s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-sandbox-card { animation: none !important; }
        }
      `}</style>
    </section>
  )
}
